/**
 * ChampSecret — une clé, un mot de passe, un jeton : masqué, un œil pour le
 * révéler, et au besoin un bouton pour le copier.
 *
 *   <ChampSecret etiquette="Clé API" value={cle} onChange={(e) => setCle(e.target.value)} />
 *   <ChampSecret etiquette="Clé de l'espace" value={cle} lectureSeule copiable aide="Ne la donne à personne." />
 *
 * C'est un `Champ` (tout ce qu'un `<input>` prend passe, `onChange` reçoit
 * l'événement natif) en `type="password"` ; l'œil bascule en `text` et dit
 * ce qu'il fait (`aria-pressed`, « Afficher » / « Masquer »). Le navigateur ne
 * propose ni de corriger ni d'enregistrer (`autoComplete="off"`, sauf si l'écran
 * en passe un autre : `"current-password"` pour une vraie connexion). La police
 * est à chasse fixe : une clé se relit caractère par caractère.
 *
 * `lectureSeule` : un secret qu'on montre sans le changer (la clé d'un espace,
 * l'adresse d'une trésorerie) — un clic dedans sélectionne tout.
 *
 * Successeur des 11 `type="password"` et des yeux écrits à la main : clés X
 * (`TwitterKeysSection`, `TwitterKeysPage`), Herald (`ComptePremiumSection`),
 * `TreasuryZone`, comptes Telegram, jeux, la clé API des cartes d'Helios.
 */
import { useState, type Ref } from "react";

import { Button } from "../Button";
import { Champ, type ChampProps } from "../Champ";
import { Eye, EyeOff } from "../icons";
import { cn } from "../utils";
import { BoutonCopier } from "./Copier";
import "./saisie.css";

export interface ChampSecretProps extends Omit<ChampProps, "type" | "lignes" | "suffixe" | "pilule"> {
  /** Un bouton pour copier la valeur, au bout. */
  copiable?: boolean;
  /** Montrer sans laisser changer. */
  lectureSeule?: boolean;
  /** Révélé dès l'affichage. */
  revele?: boolean;
  /** Les étiquettes des boutons — déjà traduites. */
  afficherLabel?: string;
  masquerLabel?: string;
  copierLabel?: string;
  copieLabel?: string;
  ref?: Ref<HTMLInputElement>;
}

export function ChampSecret({
  copiable = false,
  lectureSeule = false,
  revele: reveleAuDepart = false,
  afficherLabel = "Afficher",
  masquerLabel = "Masquer",
  copierLabel = "Copier",
  copieLabel = "Copié",
  autoComplete = "off",
  className,
  value,
  disabled,
  ref,
  ...reste
}: ChampSecretProps) {
  const [revele, setRevele] = useState(reveleAuDepart);
  const texte = value === undefined || value === null ? "" : String(value);

  const suffixe = (
    <span className="flex items-center gap-0.5">
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        className="si-dans-le-champ"
        aria-label={revele ? masquerLabel : afficherLabel}
        aria-pressed={revele}
        title={revele ? masquerLabel : afficherLabel}
        disabled={disabled || texte === ""}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setRevele((r) => !r)}
      >
        {revele ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </Button>
      {copiable && (
        <BoutonCopier valeur={texte} iconeSeule texte={copierLabel} texteCopie={copieLabel} variant="ghost" size="sm" disabled={disabled} className="si-dans-le-champ" />
      )}
    </span>
  );

  return (
    <Champ
      {...reste}
      ref={ref as Ref<HTMLInputElement | HTMLTextAreaElement>}
      type={revele ? "text" : "password"}
      value={value}
      disabled={disabled}
      readOnly={lectureSeule || reste.readOnly}
      autoComplete={autoComplete}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      suffixe={suffixe}
      className={cn("si-secret", className)}
      onFocus={(e) => {
        if (lectureSeule) (e.target as HTMLInputElement).select();
        reste.onFocus?.(e);
      }}
    />
  );
}
